import { timeOpts } from '../lib/timePrefs.js';
import { useEffect, useMemo, useState } from 'react';
import { OPT_OUT_COLUMNS, type OptOutCell, type OptOutColumnKey } from '@ehr/shared';
import { api, ApiError } from '../lib/api.js';
import { Modal } from './Modal.js';
import type { NoteUser } from './noteSharing.js';
import {
  CHIP_TEXT,
  OFF_REASONS,
  actionFor,
  auditClient,
  auditText,
  auditWho,
  chartHref,
  chipTitle,
  filterRows,
  type ConsentAuditEntry,
  type OptOutList,
  type OptOutRow,
} from '../lib/optOut.js';

/**
 * reminders#4 - the Opt-Out Center (Chase's OPT_OUT_CENTER_10-05-V1; Jay 10-08: in the EHR,
 * Phase 1). Every client's switches on core consent: care texts / email / calls, marketing
 * texts / email, and AI listening. Click one to turn it off, or back on with the client's
 * documented consent (his 7c); a STOP is a hard block that only that consent lifts. The Audit
 * log tab is the consent trail. Changes save right away (outside the modal's Save).
 */

const labelOf = (key: string) => OPT_OUT_COLUMNS.find((c) => c.key === key)?.label ?? key;

/** ehr#541: an Audit log line's client(s), each a link to their chart. */
function AuditClient({ e }: { e: ConsentAuditEntry }) {
  const { links, text } = auditClient(e);
  if (!links.length) return <>{text}</>;
  return (
    <>
      {links.map((c, i) => (
        <span key={c.id}>
          {i > 0 && ', '}
          <a className="ss-link" href={chartHref(c.id)}>
            {c.name}
          </a>
        </span>
      ))}
    </>
  );
}
const fmtWhen = (v: string) =>
  new Date(v).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...timeOpts(), // ehr#407: the person's 12/24-hour setting
  });

function SwitchDialog({
  row,
  col,
  cell,
  onClose,
  onDone,
}: {
  row: OptOutRow;
  col: OptOutColumnKey;
  cell: OptOutCell;
  onClose: () => void;
  onDone: (cells: OptOutRow['cells']) => void;
}) {
  const label = labelOf(col);
  const action = actionFor(cell);
  const [offReason, setOffReason] = useState<string>(OFF_REASONS[0]);
  const [other, setOther] = useState('');
  const [consent, setConsent] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const turningOn = action === 'on' || action === 'stop';
  const reason = turningOn ? consent.trim() : offReason === 'Other' ? other.trim() : offReason;
  const canSave = turningOn ? reason.length >= 3 : reason.length > 0;
  const save = () => {
    if (!canSave || busy) return;
    setBusy(true);
    setErr('');
    api
      .put<{ cells: OptOutRow['cells'] }>(`/consent/contacts/${row.contactId}/opt-out`, {
        column: col,
        on: turningOn,
        reason,
      })
      .then((v) => onDone(v.cells))
      .catch((e: unknown) => {
        setBusy(false);
        setErr(
          e instanceof ApiError && e.status === 403
            ? 'Your role cannot change this client.'
            : 'Could not save. Try again.',
        );
      });
  };
  const title =
    action === 'bounce'
      ? `${label} for ${row.name}`
      : turningOn
        ? `Turn ${label.toLowerCase()} back on for ${row.name}?`
        : `Turn ${label.toLowerCase()} off for ${row.name}?`;
  return (
    <Modal
      title={title}
      onClose={onClose}
      className="ooc-dialog"
      actions={
        action === 'bounce' ? (
          <button className="btn" onClick={onClose}>
            Close
          </button>
        ) : (
          <>
            <button className="btn" onClick={onClose}>
              Cancel
            </button>
            <button
              className={turningOn ? 'btn primary' : 'btn danger'}
              disabled={!canSave || busy}
              onClick={save}
            >
              {busy ? 'Saving…' : turningOn ? 'Turn back on' : 'Turn off'}
            </button>
          </>
        )
      }
    >
      {action === 'bounce' ? (
        <p className="ooc-note">
          Messages to this address bounce, so there is nothing to turn on. Correct the address in
          Profile → Contact Info.
        </p>
      ) : turningOn ? (
        <>
          {action === 'stop' ? (
            <p className="ooc-warn" role="alert">
              {row.name} texted <strong>STOP</strong>, which ends every text. Turn texts back on
              only with their documented consent. They can also text START.
            </p>
          ) : (
            <p className="ooc-note">
              Only with the client’s consent. Say how they agreed; it goes in the audit log.
            </p>
          )}
          <label className="ooc-field">
            <span>How did the client agree?</span>
            <input
              value={consent}
              onChange={(e) => setConsent(e.target.value)}
              placeholder="e.g. Signed consent form 10/08, or asked at check-in"
              maxLength={300}
              autoFocus
            />
          </label>
        </>
      ) : (
        <>
          <p className="ooc-note">
            {col === 'ai'
              ? 'AI listening stays off for this client’s sessions until it is turned back on.'
              : `${row.name} gets no ${label.toLowerCase()} until it is turned back on.`}
          </p>
          <fieldset className="ooc-reasons">
            <legend>Why</legend>
            {[...OFF_REASONS, 'Other'].map((r) => (
              <label key={r}>
                <input
                  type="radio"
                  name="ooc-off"
                  checked={offReason === r}
                  onChange={() => setOffReason(r)}
                />
                {r}
              </label>
            ))}
          </fieldset>
          {offReason === 'Other' && (
            <label className="ooc-field">
              <span>Reason</span>
              <input
                value={other}
                onChange={(e) => setOther(e.target.value)}
                maxLength={300}
                autoFocus
              />
            </label>
          )}
        </>
      )}
      {err && <p className="ooc-err">{err}</p>}
    </Modal>
  );
}

export function OptOutCenter() {
  const [tab, setTab] = useState<'clients' | 'audit'>('clients');
  const [list, setList] = useState<OptOutList | null>(null);
  const [err, setErr] = useState('');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<{ row: OptOutRow; col: OptOutColumnKey } | null>(null);
  const [audit, setAudit] = useState<ConsentAuditEntry[] | null>(null);
  const [users, setUsers] = useState<NoteUser[]>([]);

  useEffect(() => {
    let live = true;
    api
      .get<OptOutList>('/consent/opt-outs')
      .then((v) => live && setList(v))
      .catch(() => live && setErr('Could not load the opt-outs.'));
    api
      .get<NoteUser[]>('/settings/users')
      .then((u) => live && setUsers(u))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);
  useEffect(() => {
    if (tab !== 'audit') return;
    let live = true;
    api
      .get<ConsentAuditEntry[]>('/consent/audit')
      .then((v) => live && setAudit(v))
      .catch(() => live && setAudit([]));
    return () => {
      live = false;
    };
  }, [tab, list]);

  const userName = (id: string) => {
    const u = users.find((x) => x.id === id);
    return u ? [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email : 'Staff';
  };
  const rows = useMemo(() => filterRows(list?.rows ?? [], search), [list, search]);

  return (
    <div className="ooc">
      <div className="ooc-tabs" role="tablist" aria-label="Opt-Out Center">
        {(
          [
            ['clients', 'Clients'],
            ['audit', 'Audit log'],
          ] as const
        ).map(([k, l]) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={tab === k}
            className={`ooc-tab${tab === k ? ' on' : ''}`}
            onClick={() => setTab(k)}
          >
            {l}
          </button>
        ))}
      </div>

      {tab === 'clients' && (
        <>
          <div className="ooc-bar">
            <p className="ooc-hint">
              Click a switch to turn it off for a client, or back on with their consent. Changes
              save right away.
            </p>
            <input
              type="search"
              className="ooc-search"
              placeholder="Search clients by name"
              aria-label="Search clients by name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {err ? (
            <p className="muted">{err}</p>
          ) : !list ? (
            <p className="muted">Loading opt-outs…</p>
          ) : rows.length === 0 ? (
            <p className="muted">
              {search.trim() ? `No clients match “${search.trim()}”.` : 'No clients yet.'}
            </p>
          ) : (
            <div className="ooc-wrap">
              <table className="ooc-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    {list.columns.map((c) => (
                      <th key={c.key}>{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.contactId}>
                      <td className="ooc-client">
                        <div className="ooc-name">{r.name}</div>
                        <div className="ooc-sub">{r.phone || r.email || '—'}</div>
                      </td>
                      {list.columns.map((c) => {
                        const cell = r.cells[c.key];
                        const tip = chipTitle(c.label, cell);
                        return (
                          <td key={c.key} data-label={c.label}>
                            <button
                              type="button"
                              className={`ooc-chip s-${cell.state}`}
                              title={tip}
                              aria-label={`${r.name}, ${tip}`}
                              onClick={() => setOpen({ row: r, col: c.key })}
                            >
                              {CHIP_TEXT[cell.state]}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="ooc-legend">
            <span className="ooc-chip s-on">On</span> messages go out ·{' '}
            <span className="ooc-chip s-off">Off</span> turned off ·{' '}
            <span className="ooc-chip s-some">Some</span> on for some messages, off for others ·{' '}
            <span className="ooc-chip s-stop">STOP</span> the client texted STOP: no texts at all.
            Care messages go out unless the client says no; marketing only once they opt in.
          </p>
        </>
      )}

      {tab === 'audit' &&
        (audit === null ? (
          <p className="muted">Loading the audit log…</p>
        ) : audit.length === 0 ? (
          <p className="muted">No opt-out changes yet.</p>
        ) : (
          <div className="ooc-wrap">
            <table className="ooc-table ooc-audit">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Client</th>
                  <th>What</th>
                  <th>By</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {audit.map((e) => (
                  <tr key={e.id}>
                    <td data-label="When">{fmtWhen(e.at)}</td>
                    <td data-label="Client">
                      <AuditClient e={e} />
                    </td>
                    <td data-label="What">{auditText(e, labelOf)}</td>
                    <td data-label="By">{auditWho(e, userName)}</td>
                    <td data-label="Reason">
                      {typeof e.detail?.reason === 'string' && e.detail.reason
                        ? (e.detail.reason as string)
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

      {open && list && (
        <SwitchDialog
          row={open.row}
          col={open.col}
          cell={open.row.cells[open.col]}
          onClose={() => setOpen(null)}
          onDone={(cells) => {
            setList({
              ...list,
              rows: list.rows.map((r) =>
                r.contactId === open.row.contactId ? { ...r, cells } : r,
              ),
            });
            setOpen(null);
          }}
        />
      )}
    </div>
  );
}
